import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../utils/utils';

interface TablePagerProps {
    /** Zero-based index of the visible page. */
    page: number;
    rowsPerPage: number;
    /** Total number of rows across all pages. */
    count: number;
    onPageChange: (page: number) => void;
    onRowsPerPageChange: (rowsPerPage: number) => void;
    rowsPerPageOptions?: number[];
    className?: string;
}

const DEFAULT_ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

const buttonClass = 'p-1.5 rounded-[5px] text-foreground/60 transition-colors '
    + 'hover:bg-foreground/5 hover:text-foreground '
    + 'disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-foreground/60 '
    + 'disabled:cursor-default focus:outline-none focus:ring-1 focus:ring-primary';

/**
 * Shared table pager — the design-system replacement for MUI
 * `<TablePagination>`: rows-per-page control, a range label and prev/next
 * buttons. Grayscale per the design system (tenant primary only shows on the
 * focus ring); the page index is zero-based like the MUI component it replaces,
 * so callers can swap without changing their state handling.
 *
 * Legacy MUI pagers (e.g. `MasterListScaffold`) still exist and can adopt this
 * component when they are migrated.
 */
const TablePager = ({
    page,
    rowsPerPage,
    count,
    onPageChange,
    onRowsPerPageChange,
    rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
    className,
}: TablePagerProps) => {
    const { t } = useTranslation();

    const lastPage = Math.max(0, Math.ceil(count / rowsPerPage) - 1);
    const from = count === 0 ? 0 : page * rowsPerPage + 1;
    const to = Math.min(count, (page + 1) * rowsPerPage);

    const handleRowsPerPageChange = (event: ChangeEvent<HTMLSelectElement>) => {
        onRowsPerPageChange(parseInt(event.target.value, 10));
    };

    return (
        <div
            className={cn(
                'flex flex-wrap items-center justify-end gap-x-6 gap-y-2 px-4 py-2.5 text-sm text-foreground/60',
                className,
            )}
        >
            <label className={'flex items-center gap-2'}>
                {t('common.pagination.rows_per_page')}
                <select
                    value={rowsPerPage}
                    onChange={handleRowsPerPageChange}
                    className={'rounded-[5px] bg-text-contrast px-2 py-1 text-foreground '
                        + 'shadow-sm focus:border-primary focus:outline-none'}
                >
                    {rowsPerPageOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                    ))}
                </select>
            </label>

            <span>{t('common.pagination.range', { from, to, total: count })}</span>

            <div className={'flex items-center gap-1'}>
                <button
                    type={'button'}
                    aria-label={t('common.pagination.previous_page')}
                    className={buttonClass}
                    disabled={page <= 0}
                    onClick={() => onPageChange(page - 1)}
                >
                    <ChevronLeft className={'w-4 h-4'} />
                </button>
                <button
                    type={'button'}
                    aria-label={t('common.pagination.next_page')}
                    className={buttonClass}
                    disabled={page >= lastPage}
                    onClick={() => onPageChange(page + 1)}
                >
                    <ChevronRight className={'w-4 h-4'} />
                </button>
            </div>
        </div>
    );
};

export default TablePager;
